import React from "react";
import { useBootstrapTheme } from "../../contexts/BootstrapThemeContext";
import { EffectThemeTabButtonProps } from "../../types/effectTheme";

const EffectThemeTabButton= ({
  active = false,
  tabPosition = "middle",
  children,
  style,
  ref,
  ...props
}: EffectThemeTabButtonProps) => {
  const { effectiveTheme } = useBootstrapTheme();
  const isDark = effectiveTheme === "dark";

  // Input-Gradient (inaktiv)
  const inputGradient = isDark
    ? "linear-gradient(145deg, rgba(30, 41, 59, 0.75) 0%, rgba(15, 23, 42, 0.88) 100%)"
    : "linear-gradient(145deg, rgba(255, 255, 255, 0.92) 0%, rgba(248, 250, 252, 0.96) 55%, rgba(241, 245, 249, 0.92) 100%)";

  // Primary-Gradient (aktiv) – dein Button-primary Look
  const primaryGradient = isDark
    ? "linear-gradient(145deg, rgba(59,130,246,0.35), rgba(37,99,235,0.45))"
    : "linear-gradient(145deg, rgba(219,234,254,0.95), rgba(191,219,254,0.90))";
  
  const isDisabled = Boolean(props.disabled);
  const disabledStyle: React.CSSProperties = isDisabled
    ? {
        opacity: 0.4,
        cursor: "not-allowed",
        filter: "grayscale(0.65)",
        boxShadow: "none",
      }
    : {
        opacity: 1,
        cursor: "pointer",
        filter: "none",
      };

  // Rundungen: unten immer 0, oben je nach Position
  const radiusLeft = tabPosition === "left" ? "0.6rem" : "0";
  const radiusRight = tabPosition === "right" ? "0.6rem" : "0";

  const baseStyle: React.CSSProperties = {
    width: "100%",
    padding: "0.55rem 0.9rem",
    backdropFilter: "blur(10px)",
    transition: "all 0.2s ease",
    outline: "none",

    // Tab-Form: unten flach, oben optional rund
    borderTopLeftRadius: radiusLeft,
    borderTopRightRadius: radiusRight,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,

    // Wichtig für "verbinden": unten keine Border, damit der Container die übernimmt
    borderBottom: "0",

    border: isDark
      ? "1px solid rgba(96, 165, 250, 0.28)"
      : "1px solid rgba(59, 130, 246, 0.18)",

    // leichte Tiefe
    boxShadow: isDark
      ? "0 10px 26px rgba(59, 130, 246, 0.08)"
      : "0 10px 26px rgba(2, 6, 23, 0.06)",

    color: isDark ? "rgba(226,232,240,0.92)" : "rgba(15,23,42,0.92)",
    backgroundImage: active ? primaryGradient : inputGradient,
  };

  const activeStyle: React.CSSProperties = active
    ? {
        color: isDark ? "#e5f0ff" : "#0f172a",
        // aktiver Tab soll "vorne" sein
        position: "relative",
        zIndex: 2,
        boxShadow: isDark
          ? "0 14px 34px rgba(96,165,250,0.16)"
          : "0 14px 34px rgba(59,130,246,0.12)",
      }
    : {
        position: "relative",
        zIndex: 1,
      };

  return (
    <button
      {...props}
      type={props.type ?? "button"}
      style={{ ...baseStyle, ...activeStyle, ...disabledStyle, ...style }}
      ref={ref}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.transform = "translateY(-1px)";
        if (!active) {
          e.currentTarget.style.boxShadow = isDark
            ? "0 14px 34px rgba(96,165,250,0.12)"
            : "0 14px 34px rgba(59,130,246,0.10)";
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = (activeStyle.boxShadow ?? baseStyle.boxShadow) as string;
      }}
    >
      {children}
    </button>
  );
};

export default EffectThemeTabButton;
